<template>
  <v-app>
    <duck-loading
      v-if="isTestRoute || showLoadingScreen"
      :class="{ 'fade-out': fadingOut }"
    />
    <div v-if="imagesLoaded">
      <AppBar />
      <v-main>
        <router-view v-slot="{ Component }">
          <Suspense>
            <template #default>
              <component :is="Component" />
            </template>
            <template #fallback>
              <div class="d-flex justify-center align-center pa-8">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="64"
                />
              </div>
            </template>
          </Suspense>
        </router-view>
      </v-main>
      <AppFooter />
      <EmployeesDrawer />
    </div>
  </v-app>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { usePreloader } from '@/composables/usePreloader';
import art from '@/assets/art.webp';
import benefits_banner from '@/assets/benefits_banner.webp';
import benefits_mobile_banner from '@/assets/benefits_mobile_.webp';
import boardgame_wide from '@/assets/boardgame_wide.webp';
import clean_desk_banner from '@/assets/clean_desk_banner.webp';
import coaches_wide from '@/assets/coaches_wide.webp';
import coaches from '@/assets/coaches.webp';
import desk_full from '@/assets/desk_full.webp';
import Desk from '@/assets/Desk.webp';
import duck_coffee from '@/assets/hacker_duck.webp';
import duck_kanban_banner from '@/assets/duck_kanban_banner.webp';
import git from '@/assets/gitlab.webp';
import kanban_1 from '@/assets/kanban_1.webp';
import kanban_3 from '@/assets/kanban_3.webp';
import kanban_mobile from '@/assets/kanban_mobile_.webp';
import jobs_banner from '@/assets/jobs_banner.webp';
import jobs_banner_mobile from '@/assets/jobs_banner_mobile.webp';
import listening_duck from '@/assets/listening_duck.webp';
import mountain_wide from '@/assets/mountain_wide.webp';
import peer_mentoring_mountain from '@/assets/peer_mentoring_mountain.webp';
import positions_banner from '@/assets/positions_banner.webp';
import solutions_banner from '@/assets/solutions_banner.webp';
import solutions_mobile_banner from '@/assets/solutions_mobile_.webp';
import talk_draft from '@/assets/talk_draft.webp';
import us from '@/assets/us.webp';
import vibes_banner from '@/assets/vibes.webp';
import vibes_mobile_banner from '@/assets/vibes_mobile.webp';
import group_hero from '@/assets/group_hero.webp';
import group_mobile from '@/assets/group_mobile.webp';
// Vibes page images
import adventure from '@/assets/adventure.webp';
import adventure2 from '@/assets/adventure2.webp';
import adventure3 from '@/assets/adventure3.webp';
import adventure4 from '@/assets/adventure4.webp';
import fam from '@/assets/fam.webp';
import game from '@/assets/game.webp';
import nom from '@/assets/nom.webp';
import nom1 from '@/assets/nom1.webp';
import nom2 from '@/assets/nom2.webp';
import nom3 from '@/assets/nom3.webp';
import nom4 from '@/assets/nom4.webp';
import nom5 from '@/assets/nom5.webp';
import swag from '@/assets/swag.webp';
import swag2 from '@/assets/swag2.webp';
import toast from '@/assets/toast.webp';
import toast2 from '@/assets/toast2.webp';
import toast3 from '@/assets/toast3.webp';
import toast4 from '@/assets/toast4.webp';
import yellow_duck_large_small_custom from '@/assets/yellow_duck_large (Small) (Custom).webp';
import yellow_duck_large_small from '@/assets/yellow_duck_large (Small).webp';
import yellow_duck_large from '@/assets/yellow_duck_large.webp';
import yellow_duck_w50 from '@/assets/yellow_duck_w50.webp';
import yellow_duck_w75 from '@/assets/yellow_duck_w75.webp';
import yellow_duck_w100 from '@/assets/yellow_duck_w100.webp';
import yellow_duck_w225 from '@/assets/yellow_duck_w225.webp';
import { useJobStore } from '@/stores/jobStore';
import DuckLoading from '@/components/DuckLoading.vue';
import AppBar from '@/components/AppBar.vue';
import AppFooter from '@/components/AppFooter.vue';
import EmployeesDrawer from '@/components/EmployeesDrawer.vue';

export default {
  name: 'App',
  components: {
    DuckLoading,
    AppBar,
    AppFooter,
    EmployeesDrawer,
  },
  setup() {
    const route = useRoute();
    const isTestRoute = computed(() => route.path === '/loading-test');
    const jobStore = useJobStore();

    onMounted(() => {
      jobStore.fetchJobs();
    });

    const imageArray = ref([
      { src: art, name: 'art.png' },
      { src: benefits_banner, name: 'benefits_banner.png' },
      { src: benefits_mobile_banner, name: 'benefits_mobile_.png' },
      { src: boardgame_wide, name: 'boardgame_wide.png' },
      { src: clean_desk_banner, name: 'clean_desk_banner.png' },
      { src: coaches_wide, name: 'coaches_wide.png' },
      { src: coaches, name: 'coaches.png' },
      { src: desk_full, name: 'desk_full.png' },
      { src: Desk, name: 'Desk.png' },
      { src: duck_coffee, name: 'duck_coffee.png' },
      { src: duck_kanban_banner, name: 'duck_kanban_banner.png' },
      { src: git, name: 'git.png' },
      { src: kanban_1, name: 'kanban_1.png' },
      { src: kanban_3, name: 'kanban_3.png' },
      { src: kanban_mobile, name: 'kanban_mobile_.png' },
      { src: jobs_banner, name: 'jobs_banner.png' },
      { src: jobs_banner_mobile, name: 'jobs_banner_mobile.png' },
      { src: listening_duck, name: 'listening_duck.png' },
      { src: mountain_wide, name: 'mountain_wide.png' },
      { src: peer_mentoring_mountain, name: 'peer_mentoring_mountain.png' },
      { src: positions_banner, name: 'positions_banner.png' },
      { src: solutions_banner, name: 'solutions_banner.png' },
      { src: solutions_mobile_banner, name: 'solutions_mobile_.png' },
      { src: talk_draft, name: 'talk_draft.png' },
      { src: us, name: 'us.png' },
      { src: vibes_banner, name: 'vibes.png' },
      { src: vibes_mobile_banner, name: 'vibes_mobile.png' },
      { src: group_hero, name: 'group_hero.png' },
      { src: group_mobile, name: 'group_mobile.png' },
      // Vibes page images
      { src: adventure, name: 'adventure.png' },
      { src: adventure2, name: 'adventure2.png' },
      { src: adventure3, name: 'adventure3.png' },
      { src: adventure4, name: 'adventure4.png' },
      { src: fam, name: 'fam.png' },
      { src: game, name: 'game.png' },
      { src: nom, name: 'nom.png' },
      { src: nom1, name: 'nom1.png' },
      { src: nom2, name: 'nom2.png' },
      { src: nom3, name: 'nom3.png' },
      { src: nom4, name: 'nom4.png' },
      { src: nom5, name: 'nom5.png' },
      { src: swag, name: 'swag.png' },
      { src: swag2, name: 'swag2.png' },
      { src: toast, name: 'toast.png' },
      { src: toast2, name: 'toast2.png' },
      { src: toast3, name: 'toast3.png' },
      { src: toast4, name: 'toast4.png' },
      { src: yellow_duck_large_small_custom, name: 'yellow_duck_large (Small) (Custom).png' },
      { src: yellow_duck_large_small, name: 'yellow_duck_large (Small).png' },
      { src: yellow_duck_large, name: 'yellow_duck_large.png' },
      { src: yellow_duck_w50, name: 'yellow_duck_w50.png' },
      { src: yellow_duck_w75, name: 'yellow_duck_w75.png' },
      { src: yellow_duck_w100, name: 'yellow_duck_w100.png' },
      { src: yellow_duck_w225, name: 'yellow_duck_w225.png' },
    ]);

    const { imagesLoaded, showLoadingScreen, fadingOut } = usePreloader(imageArray.value);

    return {
      imagesLoaded,
      showLoadingScreen,
      fadingOut,
      isTestRoute,
    };
  },
};
</script>

<style>
@import '@/style.css';

.loading-container {
  transition: opacity 0.5s ease-in-out;
}

.fade-out {
  opacity: 0;
}

/* Custom responsive text sizing */
.text-responsive {
  font-size: 14px; /* Mobile base size */
  line-height: 1.5;
}

@media (min-width: 768px) {
  .text-responsive {
    font-size: 24.64px; /* 14px × 1.76 */
    line-height: 1.6;
  }
}
</style>
